import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useAtomValue } from "jotai";
import { devApi, observedAuthorsApi, DEV_REVEAL_ALL_KEY, type DevContext, type DevUser } from "../api/client";
import { localeAtom, type Locale } from "../stores/locale";
import { t } from "../lib/i18n";
import { apiOrigin } from "../lib/apiOrigin";

// DevMenu — non-production developer tools (membership-role / plan switching +
// per-user visibility / disclosure impersonation; is_staff is shown read-only).
// It renders ONLY when the backend confirms dev
// tools are on (devApi.context → dev_tools:true). In production the backend
// 404s /dev/context, so context() rejects and the menu stays hidden — but the
// real guarantee is server-side: the endpoints don't exist in prod. This FE
// gate is just so the button doesn't flash for a frame.

const MEMBERSHIP_ROLES = ["admin", "member"] as const;
const PLANS = ["free", "pro", "nova"] as const;
const VISIBILITIES = ["full", "anonymized", "redacted"] as const;
// Plain-language labels so the two per-user axes below don't read as cryptic
// enums. D-07 = identity erasure (does this person's git-authorship show their
// name); D-05 = signal disclosure opt-in (does a member share their scores).
const VISIBILITY_LABELS: Record<string, string> = {
  full: "full — name shown",
  anonymized: "anonymized — Member-N",
  redacted: "redacted — removed from view",
};
const CONSENT_LABELS: Record<string, string> = {
  observation: "OFF — signals hidden (opt-out)",
  full_insight: "ON — signals disclosed (opt-in)",
};
// Per-user D-05 consent is effectively a BOOLEAN opt-in: SetConsent writes
// observation (opt-out) or full_insight (opt-in), and the resolver treats any
// level > observation as "disclosed" (per-user "context" collapses to the same
// as full_insight). The three-stage ladder is the GALAXY visibility mode
// (admin), not a per-user setting — so the per-user control only exposes
// opt-out / opt-in.
const CONSENT_OPTIONS = ["observation", "full_insight"] as const;

function envLabel(ctxEnv: string): string {
  if (ctxEnv) return ctxEnv;
  if (apiOrigin.includes("stg")) return "stg";
  if (apiOrigin.includes("localhost") || apiOrigin.includes("127.0.0.1")) return "local";
  return "non-prod";
}

export default function DevMenu() {
  const { account = "" } = useParams<{ account: string }>();
  const locale = useAtomValue(localeAtom);
  const [ctx, setCtx] = useState<DevContext | null>(null);
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<DevUser[]>([]);
  // Observed-but-unjoined git authors. D-07 erasure applies to ANYONE observed
  // (not just OrbitLens-registered members), so its target list is the union of
  // members + these. D-05 consent stays members-only (only a member can opt in).
  const [observedAuthors, setObservedAuthors] = useState<{ ex_github_id: number; login: string }[]>([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  // Two-step guard for the destructive observation wipe — the first click arms
  // it, the second executes. Local UI state (manifest §1), never an atom.
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [revealAllOn, setRevealAllOn] = useState(() => {
    try {
      return localStorage.getItem(DEV_REVEAL_ALL_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Flip the local reveal-all switch and reload so every galaxy read refetches
  // with (or without) ?dev_reveal_all. The server still gates it on
  // DevToolsEnabled(), so this is inert in production even if toggled on.
  const toggleRevealAll = () => {
    const next = !revealAllOn;
    try {
      if (next) localStorage.setItem(DEV_REVEAL_ALL_KEY, "1");
      else localStorage.removeItem(DEV_REVEAL_ALL_KEY);
    } catch {
      /* ignore storage errors */
    }
    setRevealAllOn(next);
    window.location.reload();
  };

  useEffect(() => {
    if (!account) return;
    let alive = true;
    devApi
      .context(account)
      .then((c) => alive && setCtx(c?.dev_tools ? c : null))
      .catch(() => alive && setCtx(null));
    return () => {
      alive = false;
    };
  }, [account]);

  useEffect(() => {
    if (!open || !account || users.length > 0) return;
    devApi.users(account).then((r) => setUsers(r.users)).catch(() => setUsers([]));
    // Observed authors from git history (admin-gated; falls back to members-only
    // if the dev isn't an admin of this galaxy).
    observedAuthorsApi
      .list(account)
      .then((a) => setObservedAuthors((a || []).map((x) => ({ ex_github_id: x.github_user_id, login: x.login }))))
      .catch(() => setObservedAuthors([]));
  }, [open, account, users.length]);

  // D-07 erasure targets = members ∪ observed authors, deduped by ex_github_id.
  const erasureUsers = useMemo(() => {
    const byId = new Map<number, { ex_github_id: number; login: string }>();
    for (const u of users) byId.set(u.ex_github_id, { ex_github_id: u.ex_github_id, login: u.login });
    for (const a of observedAuthors) if (!byId.has(a.ex_github_id)) byId.set(a.ex_github_id, a);
    return [...byId.values()].sort((x, y) => x.login.localeCompare(y.login));
  }, [users, observedAuthors]);

  if (!ctx?.dev_tools) return null;

  // Plans valid for this galaxy type — nova is org-only, pro is personal-only,
  // free always applies. Mirrors the checkout type gate (PlanAllowedForType) so
  // dev can't set a plan the galaxy could never buy.
  const allowedPlans = PLANS.filter(
    (p) => p === "free" || (ctx.account_type === "org" ? p === "nova" : p === "pro"),
  );

  const run = async (label: string, fn: () => Promise<unknown>, reload = false) => {
    setBusy(true);
    setMsg("");
    try {
      await fn();
      setMsg(`✓ ${label}`);
      if (reload) window.location.reload();
    } catch (e) {
      setMsg(`✗ ${label}: ${e instanceof Error ? e.message : "failed"}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-4 left-4 z-[9999]" style={{ fontFamily: "var(--mono)" }}>
      {open && (
        <div
          className="mb-sm w-80 max-h-[80vh] overflow-y-auto rounded-lg border p-lg text-xs"
          style={{ background: "var(--bg-void)", borderColor: "var(--gold)", color: "var(--text-strong)" }}
        >
          <div className="mb-md flex items-center justify-between">
            <span className="font-bold" style={{ color: "var(--gold)" }}>
              DEV TOOLS · {envLabel(ctx.environment)}
            </span>
            <button onClick={() => setOpen(false)} style={{ color: "var(--text-dim)" }}>
              ✕
            </button>
          </div>

          <div className="mb-md text-gruvbox-gray">
            {ctx.user?.login} · staff={String(ctx.user?.is_staff)} · plan={ctx.plan} ·
            mrole={ctx.membership_role}
          </div>

          {/* Membership role (effective immediately, no reissue) */}
          <Section title="Membership role (active account)">
            <div className="flex flex-wrap gap-xs">
              {MEMBERSHIP_ROLES.map((r) => (
                <Chip
                  key={r}
                  active={ctx.membership_role === r}
                  disabled={busy}
                  onClick={() => run(`membership=${r}`, () => devApi.setMembershipRole(account, r), true)}
                >
                  {r}
                </Chip>
              ))}
            </div>
          </Section>

          {/* Plan */}
          <Section title="Plan">
            <div className="flex flex-wrap gap-xs">
              {allowedPlans.map((p) => (
                <Chip
                  key={p}
                  active={ctx.plan === p}
                  disabled={busy}
                  onClick={() => run(`plan=${p}`, () => devApi.setPlan(account, p), true)}
                >
                  {p}
                </Chip>
              ))}
            </div>
          </Section>

          {/* Billing state (subscription) — write a synthetic sub so the FULL
              lifecycle (active nova → lapsed past_due → canceled → free) is
              reproducible without Stripe. account.plan follows the entitlement
              projection (past_due/canceled → free) + a downgrade clears seats, so
              the lapsed-Nova banner and the free-limit fallout show up. Org-only
              (nova needs an org galaxy). */}
          {ctx.account_type === "org" && (
            <Section title="Billing state (subscription)">
              <div className="flex flex-wrap gap-xs">
                <Chip
                  active={false}
                  disabled={busy}
                  onClick={() => run("nova active", () => devApi.setSubscriptionState(account, { plan: "nova", status: "active", seats: 8 }), true)}
                >
                  Nova 稼働
                </Chip>
                <Chip
                  active={false}
                  disabled={busy}
                  onClick={() => run("nova past_due", () => devApi.setSubscriptionState(account, { plan: "nova", status: "past_due", seats: 8 }), true)}
                >
                  失効: past_due
                </Chip>
                <Chip
                  active={false}
                  disabled={busy}
                  onClick={() => run("nova canceled", () => devApi.setSubscriptionState(account, { plan: "nova", status: "canceled", seats: 8 }), true)}
                >
                  失効: canceled
                </Chip>
                <Chip
                  active={false}
                  disabled={busy}
                  onClick={() => run("free", () => devApi.setSubscriptionState(account, { plan: "free", status: "active", seats: 0 }), true)}
                >
                  Free（未加入）
                </Chip>
              </div>
            </Section>
          )}

          {/* Nova seat (self) — toggle whether YOU occupy a paid seat
              (membership.billable). Seated = full disclosure + full view;
              unseated = seat-gated (CTA / 402 needs_seat). The production seat UI
              forbids changing your own seat, so this is the only way to test both
              states as yourself. Reload so the disclosure resolver + seat gate
              re-evaluate. */}
          <Section title="Nova seat (self)">
            <div className="flex flex-wrap gap-xs">
              <Chip
                active={ctx.billable === true}
                disabled={busy}
                onClick={() => run("seat=assigned", () => devApi.setSeat(account, true), true)}
              >
                seat assigned
              </Chip>
              <Chip
                active={ctx.billable === false}
                disabled={busy}
                onClick={() => run("seat=none", () => devApi.setSeat(account, false), true)}
              >
                no seat
              </Chip>
            </div>
          </Section>

          {/* D-07 — identity erasure / "right to be forgotten". Governs whether
              a person's observed git-authorship shows their name, is anonymized
              to Member-N, or is removed from view. Applies to ANYONE observed
              (incl. departed authors / non-members); data is physically retained
              (display-layer flag). User-facing surface: the Presence page. */}
          <UserAction
            title="Identity erasure (D-07)"
            hint={t("devMenu.erasureHint", locale)}
            users={erasureUsers}
            options={[...VISIBILITIES]}
            labels={VISIBILITY_LABELS}
            busy={busy}
            onApply={(ghId, v) => run(`visibility ${v}`, () => devApi.setUserVisibility(account, ghId, v))}
          />

          {/* D-05 — signal disclosure opt-in. A current MEMBER's own choice to
              share their scores/signals with their team. Effectively boolean:
              observation = opt-out, full_insight = opt-in (the resolver treats
              any level > observation as disclosed). This is the only self
              setting a member normally has ("シグナルの開示 ON/OFF" in
              Settings). The 3-stage ladder is the galaxy-wide Visibility Mode
              (admin), NOT this. */}
          <UserAction
            title="Signal disclosure — member opt-in (D-05)"
            hint={t("devMenu.disclosureHint", locale)}
            users={users}
            options={[...CONSENT_OPTIONS]}
            labels={CONSENT_LABELS}
            busy={busy}
            onApply={(ghId, lvl) => run(`disclosure ${lvl}`, () => devApi.setDisclosure(account, lvl, ghId))}
          />

          {/* Archive universe — turn the current galaxy affiliation into a
              departed (archived) one, or restore it. Optional joined/departed
              dates shape the observed tenure window. Clearing the certificate
              lets a re-mint start clean. Acts on (caller, active account). */}
          <ArchiveAffiliation
            locale={locale}
            busy={busy}
            onArchive={(joinedAt, departedAt) =>
              run("affiliation archived", () => devApi.setAffiliation(account, { action: "archive", joinedAt, departedAt }), true)
            }
            onRestore={() =>
              run("affiliation restored", () => devApi.setAffiliation(account, { action: "restore" }), true)
            }
            onClearCertificate={() => run("certificate cleared", () => devApi.clearCertificate(account))}
          />

          {/* Revive the per-day manual-observation quota (delete the active
              galaxy's quota-counting observation_log rows in the trailing 24h),
              so observations can be triggered again without waiting for the
              window to roll. Observation snapshots themselves are untouched.
              dev-only — the endpoint 404s in prod. Reload so the quota readout
              (used/cap) refreshes. */}
          <Section title={t("devMenu.quotaTitle", locale)}>
            <p className="mb-xs text-[11px] text-gruvbox-gray">
              {t("devMenu.quotaDesc", locale)}
            </p>
            <button
              disabled={busy}
              onClick={() => run("observation quota reset", () => devApi.resetObservationQuota(account), true)}
              className="rounded px-md py-xs font-bold"
              style={{ background: "var(--gold)", color: "var(--bg-void)", opacity: busy ? 0.5 : 1 }}
            >
              {t("devMenu.quotaReset", locale)}
            </button>
          </Section>

          {/* Restart onboarding — flip the active galaxy's onboarding_completed
              back to false and reload, so Layout drops the dev back into the
              first-run flow (Onboarding gates on onboardingApi.status). dev-only;
              the endpoint 404s in prod. */}
          <Section title={t("devMenu.onboardingTitle", locale)}>
            <p className="mb-xs text-[11px] text-gruvbox-gray">
              {t("devMenu.onboardingDesc", locale)}
            </p>
            <button
              disabled={busy}
              onClick={() => run("onboarding restarted", () => devApi.resetOnboarding(account), true)}
              className="rounded px-md py-xs font-bold"
              style={{ background: "var(--gold)", color: "var(--bg-void)", opacity: busy ? 0.5 : 1 }}
            >
              {t("devMenu.onboardingRestart", locale)}
            </button>
          </Section>

          {/* Wipe observation data — physically delete THIS galaxy's observation
              rows (signals / snapshots / timeline / failures) so the never-observed
              first-observation flow can be re-run from scratch. Human-authored
              state (notes / ADRs / certificates / schedules) is preserved. The
              deliberate D-07 exception: dev-only (the endpoint 404s in prod and the
              repo refuses when ENVIRONMENT=production), two-step confirm because it
              is irreversible. Reload after so the observatory drops to the empty
              first-run state. */}
          <Section title={t("devMenu.wipeTitle", locale)}>
            <p className="mb-xs text-[11px] text-gruvbox-gray">
              {t("devMenu.wipeDesc", locale)}
            </p>
            {!confirmWipe ? (
              <button
                disabled={busy}
                onClick={() => setConfirmWipe(true)}
                className="rounded px-md py-xs font-bold"
                style={{ border: "1px solid var(--color-gruvbox-red)", color: "var(--color-gruvbox-red)", opacity: busy ? 0.5 : 1 }}
              >
                {t("devMenu.wipeButton", locale)}
              </button>
            ) : (
              <div className="flex flex-wrap gap-xs">
                <button
                  disabled={busy}
                  onClick={() =>
                    run("observation data wiped", () => devApi.wipeObservations(account), true)
                  }
                  className="rounded px-md py-xs font-bold"
                  style={{ background: "var(--color-gruvbox-red)", color: "var(--bg-void)", opacity: busy ? 0.5 : 1 }}
                >
                  {t("devMenu.wipeConfirm", locale)}
                </button>
                <button
                  disabled={busy}
                  onClick={() => setConfirmWipe(false)}
                  className="rounded px-md py-xs"
                  style={{ border: "1px solid var(--border)", color: "var(--text-muted)", opacity: busy ? 0.5 : 1 }}
                >
                  {t("devMenu.wipeCancel", locale)}
                </button>
              </div>
            )}
          </Section>

          {/* Reveal-all: bypass every disclosure control (consent, visibility,
              observation clamp) so signal values can be checked against raw data
              — including authors who aren't ace users. Persisted locally; galaxy
              reads carry ?dev_reveal_all=true. The server honors it ONLY under
              DevToolsEnabled() (stg/local), so it's inert in production. */}
          <Section title={t("devMenu.revealAllTitle", locale)}>
            <p className="mb-xs text-[11px] text-gruvbox-gray">
              {t("devMenu.revealAllDesc", locale)}
            </p>
            <button
              onClick={toggleRevealAll}
              className="rounded px-md py-xs font-bold"
              style={{
                background: revealAllOn ? "var(--gold)" : "transparent",
                color: revealAllOn ? "var(--bg-void)" : "var(--text-muted)",
                border: revealAllOn ? "none" : "1px solid var(--border)",
              }}
            >
              {revealAllOn ? t("devMenu.revealAllOn", locale) : t("devMenu.revealAllOff", locale)}
            </button>
          </Section>

          {msg && <div className="mt-md text-gruvbox-gray">{msg}</div>}
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className="rounded-full px-md py-sm text-xs font-bold shadow-lg"
        style={{ background: "var(--gold)", color: "var(--bg-void)" }}
      >
        DEV
      </button>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-md border-t pt-sm" style={{ borderColor: "var(--border)" }}>
      <div className="mb-xs text-gruvbox-gray">{title}</div>
      {children}
    </div>
  );
}

function Chip({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className="rounded px-sm py-xs"
      style={{
        border: `1px solid ${active ? "var(--gold)" : "var(--border)"}`,
        color: active ? "var(--gold)" : "var(--text-muted)",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

// ArchiveAffiliation drives the dev affiliation/cert tools. The two date inputs
// are local UI state (useState, manifest §1 — never atoms). joined/departed are
// optional YYYY-MM-DD; an empty departed defaults to now on the server.
function ArchiveAffiliation({
  locale,
  busy,
  onArchive,
  onRestore,
  onClearCertificate,
}: {
  locale: Locale;
  busy: boolean;
  onArchive: (joinedAt: string, departedAt: string) => void;
  onRestore: () => void;
  onClearCertificate: () => void;
}) {
  const [joinedAt, setJoinedAt] = useState("");
  const [departedAt, setDepartedAt] = useState("");
  const inputStyle = {
    background: "var(--surface-sunken)",
    color: "var(--text-strong)",
    border: "1px solid var(--border)",
  } as const;
  return (
    <Section title="Archive universe (affiliation)">
      <p className="mb-xs text-[11px] text-gruvbox-gray">
        {t("devMenu.archiveDesc", locale)}
      </p>
      <div className="mb-xs flex gap-xs">
        <input
          className="w-full rounded px-sm py-xs"
          style={inputStyle}
          placeholder="joined YYYY-MM-DD"
          value={joinedAt}
          onChange={(e) => setJoinedAt(e.target.value)}
        />
        <input
          className="w-full rounded px-sm py-xs"
          style={inputStyle}
          placeholder="departed YYYY-MM-DD"
          value={departedAt}
          onChange={(e) => setDepartedAt(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap gap-xs">
        <button
          disabled={busy}
          onClick={() => onArchive(joinedAt, departedAt)}
          className="rounded px-md py-xs font-bold"
          style={{ background: "var(--gold)", color: "var(--bg-void)", opacity: busy ? 0.5 : 1 }}
        >
          Archive
        </button>
        <button
          disabled={busy}
          onClick={onRestore}
          className="rounded px-md py-xs"
          style={{ border: "1px solid var(--border)", color: "var(--text-muted)", opacity: busy ? 0.5 : 1 }}
        >
          Restore
        </button>
        <button
          disabled={busy}
          onClick={onClearCertificate}
          className="rounded px-md py-xs"
          style={{ border: "1px solid var(--border)", color: "var(--text-muted)", opacity: busy ? 0.5 : 1 }}
        >
          Clear certificate
        </button>
      </div>
    </Section>
  );
}

function UserAction({
  title,
  hint,
  users,
  options,
  labels,
  busy,
  onApply,
}: {
  title: string;
  hint?: string;
  // Minimal shape so both DevUser[] (members) and the D-07 erasure union
  // (members ∪ observed authors) satisfy it.
  users: { ex_github_id: number; login: string }[];
  options: string[];
  // Optional plain-language label per option value (the <option> value stays
  // the raw enum the API expects). Falls back to the value itself.
  labels?: Record<string, string>;
  busy: boolean;
  onApply: (exGithubId: number, value: string) => void;
}) {
  const [ghId, setGhId] = useState(0);
  const [value, setValue] = useState(options[0]);
  return (
    <Section title={title}>
      {hint && <p className="mb-xs text-[11px] text-gruvbox-gray">{hint}</p>}
      <select
        className="mb-xs w-full rounded px-sm py-xs"
        style={{ background: "var(--surface-sunken)", color: "var(--text-strong)", border: "1px solid var(--border)" }}
        value={ghId}
        onChange={(e) => setGhId(Number(e.target.value))}
      >
        <option value={0}>— select user —</option>
        {users.map((u) => (
          <option key={u.ex_github_id} value={u.ex_github_id}>
            {u.login}
          </option>
        ))}
      </select>
      <div className="flex items-center gap-xs">
        <select
          className="flex-1 rounded px-sm py-xs"
          style={{ background: "var(--surface-sunken)", color: "var(--text-strong)", border: "1px solid var(--border)" }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {labels?.[o] ?? o}
            </option>
          ))}
        </select>
        <button
          disabled={busy || ghId === 0}
          onClick={() => onApply(ghId, value)}
          className="rounded px-md py-xs font-bold"
          style={{ background: "var(--gold)", color: "var(--bg-void)", opacity: busy || ghId === 0 ? 0.5 : 1 }}
        >
          set
        </button>
      </div>
    </Section>
  );
}
